import React, { useEffect, useReducer } from "react";
import { getUser } from "lib/user";

import { setWithExpiry, getWithExpiry } from "lib/localStorage";

const LOCALSTORAGE_KEY_USER = "murfy_reconditionne_user_V3";

const INITIAL_STATE = {
  token: "",
  loading: true,
  isAuthenticated: false,
  user: {
    id: null,
    role: "",
    first_name: "",
    last_name: "",
    email: "",
    email_hash: "",
    username: "",
    phone: "",
  },
  menuMobile: {
    isOpen: false,
    categoriesIsOpen: false,
  },
};

export const UserContext = React.createContext(null);

const reducer = (state, action) => {
  switch (action.type) {
    case "login": {
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        token: action.token,
      };
    }
    case "loaded": {
      return {
        ...state,
        loading: false,
      };
    }
    case "setUser": {
      return {
        ...state,
        loading: false,
        user: {
          id: action.user.id,
          first_name: action.user.first_name,
          last_name: action.user.last_name,
          email: action.user.email,
          email_hash: action.user.email_hash,
          role: action.user.role,
          phone: action.user.phone,
        },
      };
    }
    case "open": {
      return {
        ...state,
        menuMobile: {
          isOpen: true,
          categoriesIsOpen: action.categoriesIsOpen,
        },
      };
    }
    case "close": {
      return {
        ...state,
        menuMobile: {
          isOpen: false,
          categoriesIsOpen: false,
        },
      };
    }
    case "logout": {
      localStorage.removeItem(LOCALSTORAGE_KEY_USER);
      return {
        ...state,
        isAuthenticated: false,
        token: "",
        user: {},
      };
    }
    case "load": {
      return { ...action.data, loading: false };
    }
    default:
      throw new Error();
  }
};

const persistor = (state, action) => {
  const newState = reducer(state, action);
  if (action.type === "setUser") {
    setWithExpiry(LOCALSTORAGE_KEY_USER, {
      token: state.token,
      first_name: action.user.first_name,
      last_name: action.user.last_name,
    });
  }
  return newState;
};

const UserProvider = ({ children }) => {
  const reducer = useReducer(persistor, INITIAL_STATE);
  const [state, dispatch] = reducer;

  useEffect(() => {
    if (state.token) {
      getUser(state.token).then((user) => {
        dispatch({ type: "setUser", user });
      });
    }
  }, [state.token, dispatch]);

  // sync user token between browser tabs
  useEffect(() => {
    function syncToken(event) {
      if (event.key !== LOCALSTORAGE_KEY_USER) {
        return;
      }
      const data = getWithExpiry(LOCALSTORAGE_KEY_USER);
      if (data?.token) {
        dispatch({
          type: "login",
          token: data.token,
        });
      }
    }
    window.addEventListener("storage", syncToken);
    return () => {
      window.removeEventListener("storage", syncToken);
    };
  }, [dispatch]);

  // Load user from localStorage
  useEffect(() => {
    const data = getWithExpiry(LOCALSTORAGE_KEY_USER);
    if (data?.token) {
      dispatch({
        type: "login",
        token: data.token,
      });
    } else {
      dispatch({
        type: "loaded",
      });
    }
  }, [dispatch]);

  return <UserContext.Provider value={reducer}>{children}</UserContext.Provider>;
};

export default UserProvider;
